import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const AttendeeDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [attendee, setAttendee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/attendees/${id}`);
      setAttendee(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load profile");
      nav("/attendees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setInterest = async (level) => {
    setSaving(true);
    try {
      await api.post("/interest", { target_user_id: id, level });
      setAttendee((a) => ({ ...a, my_interest: level }));
      toast.success("Marked as Interested");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const removeInterest = async () => {
    setSaving(true);
    try {
      await api.delete(`/interest/${id}`);
      setAttendee((a) => ({ ...a, my_interest: null }));
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="overline">Loading…</div>
      </div>
    );
  }
  if (!attendee) return null;

  const sel = attendee.my_interest === "connect";

  return (
    <div className="min-h-screen pb-24">
      <header className="mm-sticky-header sticky top-0 z-30">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} data-testid="detail-back" className="p-1 -ml-1">
            <ArrowLeft size={20} strokeWidth={1.5} color="#0F172A" />
          </button>
          <div className="overline">Profile</div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-10 mm-fade-in">
        <div className="overline mb-2">Attendee</div>
        <h1 className="font-serif-display text-5xl leading-none" data-testid="detail-name">
          {attendee.first_name}
        </h1>
        <div className="mm-divider-gold" />
        <div className="text-base text-slate-700 mb-2" data-testid="detail-occupation">
          {attendee.occupation}
        </div>
        <div className="overline" data-testid="detail-age">Age {attendee.age}</div>

        <div className="mt-12">
          <div className="overline mb-4">Your selection</div>
          {sel ? (
            <button
              onClick={removeInterest}
              disabled={saving}
              data-testid="action-remove-button"
              className="mm-btn-primary"
            >
              <Check size={16} strokeWidth={2.5} className="inline mr-2" />
              Interested · Tap to remove
            </button>
          ) : (
            <button
              onClick={() => setInterest("connect")}
              disabled={saving}
              data-testid="action-connect-button"
              className="mm-btn-outline"
            >
              Interested
            </button>
          )}
          <p className="text-xs text-slate-500 mt-6 leading-relaxed">
            Tap <strong className="text-slate-700">Interested</strong> if you'd like to be matched with this person.
            Leave it untouched otherwise — there's no "not interested" option.
            A match only happens when <strong className="text-slate-700">both of you</strong> pick Interested.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AttendeeDetailPage;
